import { useEffect, useRef } from 'react'

interface VideoPlayerProps {
  videoUrl: string
  fileName: string
  fileSize?: number
  onClose: () => void
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

export default function VideoPlayer({ videoUrl, fileName, fileSize, onClose }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Handle click outside
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === modalRef.current) {
      onClose()
    }
  }

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = videoUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Video player: ${fileName}`}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80 backdrop-blur-[2px]"
      onClick={handleBackdropClick}
    >
      {/* Header Bar */}
      <div className="absolute top-0 left-0 w-full h-20 flex items-center justify-between px-8 z-20">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded flex items-center justify-center text-white bg-white/5 border border-white/10">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-sm font-medium text-white tracking-wide">
              {fileName}
              {fileSize != null && (
                <>
                  <span className="mx-2 text-white/40">&middot;</span>
                  <span className="text-white/60">{formatFileSize(fileSize)}</span>
                </>
              )}
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-sm text-white transition-colors group"
            title="Download video"
          >
            <svg className="w-[18px] h-[18px] text-white/70 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download
          </button>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 text-white/70 hover:text-white transition-colors"
            title="Close (Esc)"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Video */}
      <video
        ref={videoRef}
        src={videoUrl}
        controls
        autoPlay
        className="max-w-5xl w-full aspect-video bg-black rounded-lg shadow-2xl"
      >
        Your browser does not support the video tag.
      </video>

      {/* Bottom hint */}
      <div className="absolute bottom-8 text-white/40 text-sm flex items-center gap-2 select-none">
        <span className="border border-white/20 bg-white/5 px-2 py-0.5 rounded text-[10px] font-mono text-white/60 uppercase tracking-widest">Esc</span>
        to close viewer
      </div>
    </div>
  )
}
